import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { Listing, getShowSensitiveContent } from 'models/Listing';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useScrollLock from 'hooks/useScrollLock';
import { Button, ImageSlider } from 'ncoded-component-library';
import ResolveImage from 'components/ResolveImage';
import HighlightsIcon from 'icons/Highlights.icon';
import ProductsIcon from 'icons/Products.icon';
import KeyAssetsIcon from 'icons/KeyAssets.icon';
import { getImgUrl, RESIZED_WIDTH } from 'components/ResolveImage/utils';
import utils, {
  checkIsPrivate,
  checkIsUrgent,
  getAcceptedListingContacts,
  getIsListingOwner,
  getPrivateListingRoute,
  sentryError,
} from 'utils';
import EoiDate from 'components/EoiDate';
import ChatIcon from 'icons/Chat.icon';
import ListingPrices from 'components/ListingPrices';
import LockListingImage from 'assets/images/logo-locked.webp';
import LocationmarkerIcon from 'icons/Locationmarker.icon';
import { APPOINTMENT_TYPE_COPIES } from 'components/DistressedListingCard/utils';
import useSiteInitializedState from 'hooks/useSiteInitializedState';
import AdvertisingBusinessCard from 'components/AdvertisingBusinessCard';
import LockclosedIcon from 'icons/Lockclosed.icon';
import XcircleIcon from 'icons/Xcircle.icon';
import useLocationMapper, {
  LOCATION_URL_MAPPING,
} from 'router/data/useLocationMapper';
import { uuid4 } from '@sentry/utils';
import BusinessDetailsBadge from '../../components/BusinessDetailsBadge';
import {
  ImagePlaceholder,
  MainPlaceholder,
  ContactInformationPlaceholder,
} from './placeholders';
import { BUSINESS_ROUTE } from 'data/global';
import useShowEnquireAndWatchlist from 'hooks/useShowEnquireAndWatchlist';
import { AxiosError } from 'axios';
import HighlightsSection from '../../components/HighlightsSection';
import SimilarListings from '../../components/SimilarListings';
import useSponsoredListings from 'router/subrouters/Dash/subrouters/hooks/useSponsoredListings';
import useBusiness from './hooks/useBusiness';
import useMixpanelEvents from './hooks/useMixpanelEvents';
import useOpenEnquire from 'hooks/useOpenEnquire';
import ListingActions from '../../../../components/ListingActions';

import './Business.styles.scss';
import './Business.styles.responsive.scss';

type BusinessProps = {
  className?: string;
};

const isNotEmpty = (array: Array<string> | undefined) =>
  array && !!array.length;

const Business: React.FC<BusinessProps> = (props) => {
  const { className } = props;

  const { listingSlug } =
    useParams<{ listingSlug: string; listingCategory: string }>();

  const [listing, setListing] = useSiteInitializedState<Listing>(
    `listing-${listingSlug}`,
  );

  const [isLoading, setIsLoading] = useState(!listing);

  const { currentUser } = useContext(CurrentUserContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const isListingOwner = getIsListingOwner(
    currentUser?._id,
    listing?.userId,
    listing?.isCoOwner,
  );

  const listingId = listing?._id || '';
  const dealRoomId = listing?.dealRoomId || '';

  const classes = classNames('business', className);

  const { locationLabelMapper } = useLocationMapper();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { unlockScroll } = useScrollLock();

  const { HelmetContent, HeadingNavigation } = useBusiness(listing);

  const { rightRailAd } = useSponsoredListings(listingSlug);

  const { showEnquire } = useShowEnquireAndWatchlist(listing);

  const isSensitiveContentShown = useMemo(
    () => getShowSensitiveContent(listing),
    [listing],
  );

  const openEnquire = useOpenEnquire();

  const handleNotInterested = useCallback(async () => {
    try {
      await api.listings.declineInterest(listingId);
      navigate(BUSINESS_ROUTE, { replace: true });
    } catch (error) {
      sentryError(error);
    }
  }, [listingId, navigate]);

  // unlock scroll if user reroutes while slider open:
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => unlockScroll(), []);

  // fetch listing:
  useEffect(() => {
    !utils.isSSGHuman() &&
      listingSlug &&
      (async () => {
        // In case
        setListing((old) => {
          if (!old) setIsLoading(true);
          return old;
        });
        try {
          const {
            data: { listing },
          } = await api.listings.getListingWithSlug(listingSlug, {
            $relations: '["dealRoom"]',
          });

          setListing(listing);
        } catch (e) {
          const error = e as AxiosError & { listingId?: string };

          const listingId = error?.response?.data?.error?.listingId;

          if (listingId) {
            navigate(getPrivateListingRoute(listingId), {
              replace: true,
            });
          } else {
            navigate('/page-not-found', { replace: true });
          }
        } finally {
          setIsLoading(false);
        }
      })();
  }, [listingSlug, navigate, popServerError, setListing]);

  const onWatchlisted = useCallback(
    (isWatchlisted) => setListing((old) => ({ ...old, isWatchlisted })),
    [setListing],
  );

  useMixpanelEvents({
    listing,
    listingId,
    dealRoomId,
    isListingOwner,
  });

  const EnquireButton = useMemo(
    () => (
      <Button icon={ChatIcon} onClick={() => openEnquire(listing)}>
        {t('enquire')}
      </Button>
    ),
    [listing, openEnquire, t],
  );

  const RenderedListingActions = useMemo(
    () => <ListingActions listing={listing} onWatchlisted={onWatchlisted} />,
    [listing, onWatchlisted],
  );

  const RenderExpiredEmptyState = useMemo(
    () => (
      <div className="resolve-agent-details">
        <p className="empty-title">{t('lookingToBuyBusiness')}</p>
        <Link to={BUSINESS_ROUTE}>{t('browseOurList')}</Link>
      </div>
    ),
    [t],
  );

  const RenderPresentedBy = useMemo(() => {
    const { appointmentType, type } = listing || {};

    if (!checkIsUrgent(type))
      return <p className="title">{t('presentedBy')}</p>;

    return (
      <p className="title">
        {t(
          (APPOINTMENT_TYPE_COPIES[
            appointmentType as keyof typeof APPOINTMENT_TYPE_COPIES
          ] as string) || APPOINTMENT_TYPE_COPIES.Other,
        )}
      </p>
    );
  }, [listing, t]);

  const acceptedListingContacts = useMemo(
    () => getAcceptedListingContacts(listing?.listingContacts),
    [listing?.listingContacts],
  );

  const RenderAgentDetails = useMemo(
    () =>
      acceptedListingContacts.length ? (
        <div className="resolve-agent-details">
          {RenderPresentedBy}

          <div className="resolve-agent-details__image-wrapper">
            {listing.listingContact?.firmLogo?.url && (
              <ResolveImage
                asCompanyLogo
                src={getImgUrl(
                  listing.listingContact?.firmLogo?.url,
                  RESIZED_WIDTH[480],
                )}
              />
            )}
          </div>

          <ul>
            {acceptedListingContacts.map(({ name, position, brokerImage }) => (
              <li key={uuid4()}>
                <div className="res-lc-content">
                  <span>{name}</span>
                  <span>{position}</span>
                </div>
                {brokerImage?.url && (
                  <ResolveImage
                    src={getImgUrl(brokerImage.url, RESIZED_WIDTH[240])}
                  />
                )}
              </li>
            ))}
          </ul>
          {RenderedListingActions}
        </div>
      ) : (
        // details empty state
        <div className="resolve-agent-details">
          <div className="resolve-agent-details__image-wrapper">
            <ResolveImage src={LockListingImage} asCompanyLogo />
          </div>
          <p className="empty-title">{t('lockEnquireTitle')}</p>
          <p className="empty-description">{t('lockEnquireDescription')}</p>
          {RenderedListingActions}
        </div>
      ),
    [
      RenderPresentedBy,
      RenderedListingActions,
      listing,
      acceptedListingContacts,
      t,
    ],
  );

  const RenderHighlightsSection = useMemo(() => {
    const { highlights, products, keyAssets } = listing || {};

    return (
      listing &&
      isSensitiveContentShown && (
        <div className="business__main__highlights">
          {isNotEmpty(highlights) && (
            <HighlightsSection
              titleLabel="highlights"
              listArray={highlights}
              icon={HighlightsIcon}
            />
          )}

          {isNotEmpty(products) && (
            <HighlightsSection
              titleLabel="products"
              listArray={products}
              icon={ProductsIcon}
            />
          )}

          {isNotEmpty(keyAssets) && (
            <HighlightsSection
              titleLabel="keyAssets"
              listArray={keyAssets}
              icon={KeyAssetsIcon}
            />
          )}
        </div>
      )
    );
  }, [isSensitiveContentShown, listing]);

  const ImageContent = useMemo(
    () =>
      isLoading ? (
        <div className="res-ldp-image-container">
          <ImagePlaceholder />
        </div>
      ) : (
        listing && (
          <div className="res-ldp-image-container">
            {isSensitiveContentShown ? (
              listing.gallery?.length > 1 ? (
                <ImageSlider
                  slides={listing.gallery.map(({ url }) => ({
                    imageUrl: getImgUrl(url, RESIZED_WIDTH[1024]),
                  }))}
                  showDots={!!listing.gallery?.length}
                  className="listing-card-resolve__image-slider-container__slider"
                />
              ) : (
                <ResolveImage
                  className="listing-card-image"
                  src={getImgUrl(
                    listing.gallery?.[0]?.url,
                    RESIZED_WIDTH[1024],
                  )}
                />
              )
            ) : (
              <ResolveImage className="listing-card-image" />
            )}
          </div>
        )
      ),
    [isLoading, isSensitiveContentShown, listing],
  );

  const MainCardContent = useMemo(
    () =>
      isLoading ? (
        <MainPlaceholder />
      ) : (
        listing && (
          <div className="business__main">
            <h1>{listing.listingTitle}</h1>
            <div className="business__main__links">
              {listing?.location?.state ? (
                <Link
                  to={`${BUSINESS_ROUTE}/${utils.createSlug(
                    LOCATION_URL_MAPPING[listing.location.state],
                  )}`}
                >
                  {locationLabelMapper[listing.location.state]}
                </Link>
              ) : listing?.locationPostcode ? (
                <span className="postcode">
                  <LocationmarkerIcon />
                  {listing?.locationPostcode}
                </span>
              ) : (
                listing?.dealLocation
              )}
              <Link
                to={`${BUSINESS_ROUTE}/${utils.createSlug(listing.industry)}`}
              >
                {listing.industry}
              </Link>
            </div>
            <EoiDate
              bigger
              seekingExpressionOfInterestDate={
                listing.seekingExpressionOfInterestDate
              }
            />
            {isSensitiveContentShown && (
              <ListingPrices
                askingPrice={listing?.askingPriceSelect}
                amount={listing?.amount}
                amountFrom={listing?.amountFrom}
                amountTo={listing?.amountTo}
                revenue={listing?.revenue}
                EBITDA={listing?.EBITDA}
                averageStockValue={listing?.averageStockValue}
                stockAtValue={listing?.stockAtValue}
                currency={listing?.currency}
                amountRequiredVisibleInfo={listing?.amountRequiredVisibleInfo}
                amountFromRequiredVisibleInfo={
                  listing?.amountFromRequiredVisibleInfo
                }
                amountToRequiredVisibleInfo={
                  listing?.amountToRequiredVisibleInfo
                }
                revenueRequiredVisibleInfo={listing?.revenueRequiredVisibleInfo}
                EBITDARequiredVisibleInfo={listing?.EBITDARequiredVisibleInfo}
                averageStockValueRequiredVisibleInfo={
                  listing?.averageStockValueRequiredVisibleInfo
                }
              />
            )}
            <p className="business__main__description">
              {listing.businessDescription}
            </p>

            {RenderedListingActions}

            {RenderHighlightsSection}
          </div>
        )
      ),
    [
      isLoading,
      listing,
      locationLabelMapper,
      isSensitiveContentShown,
      RenderedListingActions,
      RenderHighlightsSection,
    ],
  );

  return (
    <div className={classes}>
      {HelmetContent}
      {HeadingNavigation}

      <div className="business__heading">
        {isLoading ? null : (
          <>
            {checkIsPrivate(listing?.stage) && listing?.isPrivateViewer && (
              <div className="business__heading__private-listing">
                <LockclosedIcon />
                <div>
                  <p>{t('privateListingBannerTitle')}</p>
                  <p>{t('privateListingBannerDescription')}</p>
                </div>
                {showEnquire && EnquireButton}
                <Button
                  variant="outline"
                  icon={XcircleIcon}
                  onClick={handleNotInterested}
                >
                  {t('notInterested')}
                </Button>
              </div>
            )}
            <BusinessDetailsBadge
              stage={listing.stage}
              dealStage={listing.dealRoom?.stage}
            />
          </>
        )}
      </div>

      {ImageContent}

      {MainCardContent}

      <aside>
        <div>
          {isLoading ? (
            <ContactInformationPlaceholder />
          ) : isSensitiveContentShown ? (
            RenderAgentDetails
          ) : (
            RenderExpiredEmptyState
          )}
          {rightRailAd && (
            <AdvertisingBusinessCard
              title={rightRailAd.title}
              description={rightRailAd.description}
              coverPhoto={rightRailAd.coverPhoto}
              logoImage={rightRailAd.logoImage}
              link={rightRailAd.link}
              ctaText={rightRailAd.ctaText}
              onlyMobileView
            />
          )}
        </div>
      </aside>

      <SimilarListings listingId={listingId} isLoading={isLoading} />
    </div>
  );
};

export default Business;
